import { useState, useRef } from 'react';
import { GpxParser } from '../data/gpx-parser';
import { GpsSimulator } from '../data/gps-simulator';
import type { GpsPosition } from '../sensors/gps';

interface SimulationHeaderProps {
  onPositionUpdate: (position: GpsPosition) => void;
  onSimulationStateChange: (isSimulating: boolean) => void;
  deferred: BeforeInstallPromptEvent | null;
  installed: boolean;
  swReady: boolean;
  onInstall: () => void;
}

// Liste statique des traces GPX disponibles
const availableTraces = [
  { file: '20250901-064419 - Voyage vers Molsheim.gpx', label: '01/09/25 Voyage vers Molsheim' },
  { file: '20250902-122156 - Strasb molsheim.gpx', label: '02/09/25 Strasb molsheim' },
  { file: '20250904-071230 - Molsheim Strasbourg.gpx', label: '04/09/25 Molsheim Strasbourg' },
  { file: '20250904-073409 - Stb metz.gpx', label: '04/09/25 Stb metz' },
  { file: '20250904-142618 - Creutz.gpx', label: '04/09/25 Creutz' },
  { file: '20250904-154514 - Creutz2.gpx', label: '04/09/25 Creutz2' },
  { file: '20250904-164548 - Creutz Sarrebruck.gpx', label: '04/09/25 Creutz Sarrebruck' },
  { file: '20250904-180620 - Sarrebruck forback.gpx', label: '04/09/25 Sarrebruck forback' },
  { file: '20250904-182731 - Forbach metz.gpx', label: '04/09/25 Forbach metz' },
  { file: '20250904-194246 - Str mol.gpx', label: '04/09/25 Str mol' }
];

export function SimulationHeader({ 
  onPositionUpdate, 
  onSimulationStateChange, 
  deferred, 
  installed, 
  swReady, 
  onInstall 
}: SimulationHeaderProps) {
  const [isSimulating, setIsSimulating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [traceName, setTraceName] = useState('');
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // Test en temps réel
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState('default');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const simulatorRef = useRef<GpsSimulator | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.gpx')) {
      alert('Veuillez sélectionner un fichier GPX');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const gpxContent = e.target?.result as string;
        startSimulationWithGPX(gpxContent);
        setShowPopup(false);
        
      } catch (error) {
        console.error('Erreur lecture GPX:', error);
        alert('Erreur lors de la lecture du fichier GPX');
      }
    };
    
    reader.readAsText(file);
  };

  const handleTraceSelect = async () => {
    if (selectedTrace === 'default') return;
    
    try {
      const response = await fetch(`/gpx/${selectedTrace}`);
      const gpxContent = await response.text();
      startSimulationWithGPX(gpxContent);
      setShowPopup(false);
      
    } catch (error) {
      console.error('Erreur chargement trace:', error);
      alert('Erreur lors du chargement de la trace');
    }
  };

  const startSimulationWithGPX = (gpxContent: string) => {
    // Arrêter proprement la simulation précédente et réinitialiser le bearing
    if (simulatorRef.current) {
      simulatorRef.current.stop();
      simulatorRef.current = null;
      
      // Réinitialiser le bearing immédiatement
      import('../map/maplibre').then(({ bearingController }) => {
        bearingController.resetBearing();
      });
      
      // Petit délai pour laisser le temps au système de se stabiliser
      setTimeout(() => {
        startNewSimulation(gpxContent);
      }, 300);
      return;
    }
    
    startNewSimulation(gpxContent);
  };
  
  const startNewSimulation = (gpxContent: string) => {
    // Parser et filtrer la trace GPX
    const trace = GpxParser.parse(gpxContent);
    
    // Démarrer le simulateur avancé au lieu du simple
    simulatorRef.current = new GpsSimulator({
      onPosition: onPositionUpdate,
      onError: (error) => {
        console.error('Erreur simulation GPS:', error);
      },
      playbackSpeed: playbackSpeed
    });
    
    simulatorRef.current.loadTrace(trace);
    setIsSimulating(true);
    setIsPaused(false);
    setTraceName(trace.name || 'Simulation GPX');
    onSimulationStateChange(true);
    
    // Configurer la fréquence POI PK dès le démarrage
    import('../map/poi-layer').then(({ poiLayer }) => {
      poiLayer.setReloadFrequency(playbackSpeed);
    });
    
    // Suivi de progression
    const progressInterval = setInterval(() => {
      if (simulatorRef.current?.isActive()) {
        const prog = simulatorRef.current.getCurrentProgress();
        setProgress(prog);
      } else {
        clearInterval(progressInterval);
        setProgress({ current: 0, total: 0, percentage: 0 });
        setIsSimulating(false);
        setIsPaused(false);
        onSimulationStateChange(false);
      }
    }, 1000);
    
    console.log('🚂 Simulation avancée démarrée');
  };

  const handlePlayPause = () => {
    if (!simulatorRef.current) return;
    
    if (isPaused) {
      simulatorRef.current.play();
      setIsPaused(false);
    } else {
      simulatorRef.current.pause();
      setIsPaused(true);
    }
  };

  const handleStop = () => {
    if (simulatorRef.current) {
      simulatorRef.current.stop();
      setIsSimulating(false);
      setIsPaused(false);
      setProgress({ current: 0, total: 0, percentage: 0 });
      setTraceName('');
      onSimulationStateChange(false);
      console.log('🚂 Simulation arrêtée');
      
      // Réinitialiser l'orientation de la carte et fréquence POI PK
      // Import dynamique pour éviter les dépendances circulaires
      import('../map/maplibre').then(({ bearingController }) => {
        bearingController.resetBearing();
      });
      import('../map/poi-layer').then(({ poiLayer }) => {
        poiLayer.setReloadFrequency(1); // Revenir à la fréquence normale
      });
    }
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
    if (simulatorRef.current) {
      simulatorRef.current.setPlaybackSpeed(newSpeed);
    }
    
    // Ajuster la fréquence de rechargement des POI PK
    import('../map/poi-layer').then(({ poiLayer }) => {
      poiLayer.setReloadFrequency(newSpeed);
    });
  };

  return (
    <>
      <header className="px-4 py-2 border-b border-neutral-800 flex items-center gap-3 z-20 relative">
        {/* Bouton Simu. ancré à gauche */}
        <button
          onClick={() => setShowPopup(true)}
          className="px-2 py-1 text-xs rounded bg-white/10 hover:bg-white/20 transition-colors"
        >
          Simu.
        </button>

        {/* Contrôles de simulation quand une trace est chargée */}
        {isSimulating && (
          <>
            <button
              onClick={handlePlayPause}
              className="px-2 py-1 text-xs rounded bg-green-600/80 hover:bg-green-600 transition-colors"
            >
              {isPaused ? '▶' : '⏸'}
            </button>
            
            <button
              onClick={handleStop}
              className="px-2 py-1 text-xs rounded bg-red-600/80 hover:bg-red-600 transition-colors"
            >
              ⏹
            </button>
            
            <select
              value={playbackSpeed}
              onChange={(e) => handleSpeedChange(Number(e.target.value))}
              className="bg-black/50 text-white text-xs border border-white/20 rounded px-1"
            >
              <option value={1}>x1</option>
              <option value={5}>x5</option>
              <option value={10}>x10</option>
              <option value={20}>x20</option>
              <option value={30}>x30</option>
            </select>

            <div className="text-xs text-white/60">
              {traceName} ({progress.percentage}%)
            </div>
          </>
        )}

        {/* Bouton installer */}
        <div className="ml-auto flex items-center gap-2">
          {deferred && !installed && (
            <button
              onClick={onInstall}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-xs"
            >
              Install
            </button>
          )}
          {!deferred && !installed && (
            <span className="text-xs text-neutral-400">
              {swReady ? "PWA prête" : "Init…"}
            </span>
          )}
          {installed && <span className="text-xs text-emerald-400">Installée</span>}
        </div>
      </header>

      {/* Popup de simulation */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Simulation GPS</h2>
            
            {/* Bouton charger fichier */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full mb-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              Charger un fichier GPX
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".gpx"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Menu déroulant des traces */}
            <div className="mb-4">
              <select
                value={selectedTrace}
                onChange={(e) => setSelectedTrace(e.target.value)}
                className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-2 text-sm"
              >
                <option value="default">Lancer une trace existante</option>
                {availableTraces.map((trace) => (
                  <option key={trace.file} value={trace.file}>
                    {trace.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleTraceSelect}
                disabled={selectedTrace === 'default'}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-600 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                Lancer
              </button>
              
              <button
                onClick={() => setShowPopup(false)}
                className="px-4 py-2 bg-neutral-600 hover:bg-neutral-700 text-white rounded transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}